import React, { useState, useEffect } from "react";
import { Layout, Menu, Image, Space, Drawer, Button } from "antd";
import {
	PieChartOutlined,
	TransactionOutlined,
	ApartmentOutlined,
	LockOutlined,
	MenuOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Breadcrumbs from "./Breadcrumbs";
import Logo from "../assets/images/MarshalLogo.png";
import LogoMini from "../assets/images/MarshalMiniLogo.png";
import isCan from "./isCan";
import Cookies from "js-cookie";

const { Header, Content, Footer, Sider } = Layout;

const breadcrumbMap = {
	wplywy: {
		name: "Wpływy",
		children: {
			"*": {
				name: "Wpływ :id",
				dynamic: true,
			},
		},
	},
	wplywybezprzekazania: {
		name: "Wpływy bez przekazania",
		children: {
			"*": {
				name: "Wpływ :id",
				dynamic: true,
			},
		},
	},
	rezerwy: {
		name: "Rezerwy",
		children: {
			"*": {
				name: "Rezerwa :id",
				dynamic: true,
			},
		},
	},
	przelewy: {
		name: "Przelewy do wygenerowania",
		children: {
			"*": {
				name: "Przelew :id",
				dynamic: true,
			},
		},
	},
	wygenerowane: {
		name: "Przelewy wygenerowane",
		children: {
			"*": {
				name: "Przelew :id",
				dynamic: true,
			},
		},
	},
	przekazy: {
		name: "Przekazy do wygenerowania",
		children: {
			"*": {
				name: "Przekaz :id",
				dynamic: true,
			},
		},
	},
	"przekazy-wygenerowane": {
		name: "Przekazy wygenerowane",
		children: {
			"*": {
				name: "Przekaz :id",
				dynamic: true,
			},
		},
	},
	pozyczki: {
		name: "Pożyczki",
		children: {
			"*": {
				name: "Pożyczka :id",
				dynamic: true,
			},
		},
	},
};

const AppLayout = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useAuth(); // Use user context
	const [collapsed, setCollapsed] = useState(false);
	const [opened, setOpened] = useState(false);
	const [items, setItems] = useState([]);

	// Generate menu items dynamically based on user permissions
	const generateItems = async (userPerms) => {
		return [
			(await isCan(["can_access_wplyw"], userPerms)) && {
				key: "/wplywy",
				icon: <PieChartOutlined />,
				label: "Wpływy",
				url: "/wplywy",
			},
			(await isCan(["can_access_wplyw"], userPerms)) && {
				key: "/wplywy-bez-przekazania",
				icon: <PieChartOutlined />,
				label: "Wpływy bez przekazania",
				url: "/wplywy-bez-przekazania",
			},
			(await isCan(["can_access_wplyw"], userPerms)) && {
				key: "/przekazy",
				icon: <TransactionOutlined />,
				label: "Przekazy do wygenerowania",
				url: "/przekazy",
			},
			(await isCan(["can_access_wplyw"], userPerms)) && {
				key: "/przekazy-wygenerowane",
				icon: <LockOutlined />,
				label: "Przekazy wygenerowane",
				url: "/przekazy-wygenerowane",
			},
			(await isCan(["can_access_wplyw"], userPerms)) && {
				key: "/pozyczki",
				icon: <TransactionOutlined />,
				label: "Pożyczki",
				url: "/pozyczki",
			},
			(await isCan(["can_access_reserves"], userPerms)) && {
				key: "/rezerwy",
				icon: <ApartmentOutlined />,
				label: "Rezerwy",
				url: "/rezerwy",
			},
			(await isCan(["can_access_transfers"], userPerms)) && {
				key: "/przelewy",
				icon: <TransactionOutlined />,
				label: "Przelewy do wygenerowania",
				url: "/przelewy",
			},
			(await isCan(["can_access_transfers_generated"], userPerms)) && {
				key: "/wygenerowane",
				icon: <LockOutlined />,
				label: "Przelewy wygenerowane",
				url: "/wygenerowane",
			},
			(await isCan(["can_change_settings"], userPerms)) && {
				key: "/ustawienia-systemu",
				icon: <SettingOutlined />,
				label: "Ustawienia systemu",
				url: "/ustawienia-systemu",
			},
		].filter(Boolean); // Remove null/undefined entries
	};

	useEffect(() => {
		const fetchItems = async () => {
			if (user) {
				const userPerms = user.perms || []; // Handle undefined user
				const generatedItems = await generateItems(userPerms);
				setItems(generatedItems);
			}
		};
		fetchItems();
	}, [user]); // Re-run when user is updated

	const handleMenuClick = ({ key }) => {
		const selectedItem = items.find((item) => item.key === key);
		if (selectedItem?.url) {
			setOpened(false);
			navigate(selectedItem.url);
		}
	};

	const { content } = props;

	// If user is still loading or not available, return nothing
	if (!user?.user) {
		return null;
	}

	return (
		<Layout
			style={{
				minHeight: "100vh",
			}}
		>
			<Drawer
				title=""
				placement="right"
				onClose={() => setOpened(false)}
				open={opened}
			>
				<Space style={{ padding: "20px" }}>
					<Image
						preview={false}
						src={!collapsed ? Logo : LogoMini}
					/>
				</Space>
				<Menu
					selectedKeys={[location.pathname]}
					defaultSelectedKeys={[""]}
					mode="inline"
					items={items}
					onClick={handleMenuClick}
				/>
			</Drawer>
			<Sider
				className="sider"
				style={{ background: "white", width: "400px" }}
				collapsible
				width="250px"
				collapsed={collapsed}
				onCollapse={(value) => setCollapsed(value)}
			>
				<Space style={{ padding: "20px" }}>
					<Image
						preview={false}
						src={!collapsed ? Logo : LogoMini}
					/>
				</Space>
				<Menu
					selectedKeys={[location.pathname]}
					mode="inline"
					items={items}
					onClick={handleMenuClick}
				/>
			</Sider>
			<Layout>
				<Header
					style={{
						padding: "0 20px",
						background: "white",
						display: "flex",
						gap: "15px",
						justifyContent: "flex-end",
						alignItems: "center",
					}}
				>
					<Button
						onClick={() => {
							Cookies.set("mgltokenprevious", null, {
								expires: 7,
								domain: process.env.REACT_APP_DOMAIN,
							});
							Cookies.remove("mgltokenprevious", { path: "/" }); // Add path if needed
							Cookies.set("mgltoken", null, {
								expires: 7,
								domain: process.env.REACT_APP_DOMAIN,
							});
							Cookies.remove("mgltoken", { path: "/" }); // Add path if needed
							window.location.href = "/";
						}}
					>
						Wyloguj się
					</Button>
					<Button
						onClick={() => setOpened(true)}
						className="mobilemenu"
						icon={<MenuOutlined />}
					/>
				</Header>
				<Content>
					<Breadcrumbs breadcrumbMap={breadcrumbMap} />
					<div style={{ background: "white" }}>{content}</div>
				</Content>
				<Footer style={{ textAlign: "center" }}>
					Weblance x Marshal Copyright
				</Footer>
			</Layout>
		</Layout>
	);
};

export default AppLayout;
